import { AlertCircle, BarChart3 } from 'lucide-react-native';
import { ShimmerSkeleton } from '../../../components/animations/Skeleton';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Card } from '../../../components/primitives/Card';
import { Grid } from '../../../components/primitives/Grid';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';

export function MetricCardSkeleton() {
  return (
    <Card
      variant="default"
      padding="xl"
      radius="xl"
      style={{
        height: 160,
        width: '100%',
        alignSelf: 'stretch',
      }}
    >
      <VStack spacing="md" style={{ flex: 1, justifyContent: 'space-between' }}>
        <VStack spacing="md">
          <ShimmerSkeleton width={80} height={12} borderRadius={4} />
          <ShimmerSkeleton width={140} height={28} borderRadius={6} />
        </VStack>
        <ShimmerSkeleton width={100} height={12} borderRadius={4} />
      </VStack>
    </Card>
  );
}

export function TabLoadingState({ cardCount = 4 }: Readonly<{ cardCount?: number }>) {
  return (
    <VStack spacing="xl">
      <Grid columns={{ mobile: 1, tablet: 2, desktop: 4 }} spacing="xl">
        {Array.from({ length: cardCount }, (_, i) => (
          <MetricCardSkeleton key={i} />
        ))}
      </Grid>
      <Card
        variant="default"
        padding="xl"
        radius="xl"
        style={{ minHeight: 240, width: '100%', alignSelf: 'stretch' }}
      >
        <VStack spacing="lg">
          <HStack justify="between" align="center">
            <ShimmerSkeleton width={180} height={16} borderRadius={4} />
            <ShimmerSkeleton width={60} height={12} borderRadius={4} />
          </HStack>
          <ShimmerSkeleton height={180} borderRadius={8} />
        </VStack>
      </Card>
    </VStack>
  );
}

interface TabErrorStateProps {
  onRetry: () => void;
}

export function TabErrorState({ onRetry }: Readonly<TabErrorStateProps>) {
  const { t } = useTranslation();

  return (
    <Card
      variant="default"
      padding="xl"
      radius="xl"
      style={{ width: '100%', alignSelf: 'stretch' }}
    >
      <VStack spacing="lg" align="center" style={{ paddingVertical: 24 }}>
        <Box
          width={56}
          height={56}
          backgroundColor={colors.status.error.bg}
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
        >
          <AlertCircle size={28} color={colors.status.error.main} strokeWidth={2} />
        </Box>
        <VStack spacing="xs" align="center">
          <Text
            variant="h6"
            color={colors.text.primary}
            style={{ fontWeight: '600', textAlign: 'center' }}
          >
            {t('analytics.states.errorTitle')}
          </Text>
          <Text
            variant="body"
            color={colors.text.tertiary}
            style={{ textAlign: 'center', maxWidth: 320 }}
          >
            {t('analytics.states.errorMessage')}
          </Text>
        </VStack>
        <Button
          variant="secondary"
          onPress={onRetry}
          accessibilityLabel={t('analytics.states.retryAccessibilityLabel')}
        >
          {t('analytics.states.retry')}
        </Button>
      </VStack>
    </Card>
  );
}

export function TabEmptyState({ message }: Readonly<{ message?: string }>) {
  const { t } = useTranslation();

  return (
    <Card
      variant="default"
      padding="xl"
      radius="xl"
      style={{ width: '100%', alignSelf: 'stretch' }}
    >
      <VStack spacing="lg" align="center" style={{ paddingVertical: 32 }}>
        <Box
          width={56}
          height={56}
          backgroundColor={colors.primary.muted}
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
        >
          <BarChart3 size={28} color={colors.primary.neon} strokeWidth={2} />
        </Box>
        <VStack spacing="xs" align="center">
          <Text
            variant="h6"
            color={colors.text.primary}
            style={{ fontWeight: '600', textAlign: 'center' }}
          >
            {t('analytics.states.emptyTitle')}
          </Text>
          <Text
            variant="body"
            color={colors.text.tertiary}
            style={{ textAlign: 'center', maxWidth: 320 }}
          >
            {message ?? t('analytics.states.emptyMessage')}
          </Text>
        </VStack>
      </VStack>
    </Card>
  );
}
